import type { FoodFeedback, FoodFeedbackResult, FoodPreference, FoodPreferenceResult, FoodRecommendation, FoodRecommendationList } from "@/shared/contracts";
import type { FoodDiscoveryService, ServiceContext } from "@/shared/services/types";
import { FOOD_VARIANTS, fixturePreference, fixtureRecommend, fixtureRecommendation } from "@/fixtures/food";
import { ApiError } from "@/shared/api/errors";
import { delay } from "@/fixtures/world";

export function createLiveFoodService({ api }: ServiceContext): FoodDiscoveryService {
  return {
    preferenceResult: (petId, subject, key) => api.request<FoodPreferenceResult>(`/food/preferences/${subject}/result`, { query: { pet_id: petId }, idempotencyKey: key }),
    preference: (petId, subject) => api.request<FoodPreference>(`/food/preferences/${subject}`, { query: { pet_id: petId } }),
    recommend: (body) => api.request<FoodRecommendationList>("/food/recommendations", { method: "POST", body }),
    recommendation: (id) => api.request<FoodRecommendation>(`/food/recommendations/${encodeURIComponent(id)}`),
    feedbackResult: (recommendationId, petId, key) => api.request<FoodFeedbackResult>(`/food/feedback/${encodeURIComponent(recommendationId)}/result`, { query: { pet_id: petId }, idempotencyKey: key }),
    feedback: (body, key) => api.request<FoodFeedback>("/food/feedback", { method: "POST", body, idempotencyKey: key }),
    savePreference: (subject, preference, key) =>
      api.request<FoodPreference>(`/food/preferences/${subject}`, { method: "PUT", body: preference, idempotencyKey: key }),
    fixtureVariants: () => [],
  };
}

export function createFixtureFoodService(): FoodDiscoveryService {
  // Synthetic same-adapter memory only; a rebuilt adapter cannot invent an old operation.
  const receipts = new Map<string, FoodPreference>();
  const feedbackReceipts = new Map<string, FoodFeedback>();
  const receiptKey = (petId: string, subject: string, key: string) => JSON.stringify([petId, subject, key]);
  return {
    preferenceResult: (petId, subject, key) => {
      const preference = receipts.get(receiptKey(petId, subject, key));
      return delay(preference ? { state: "completed" as const, preference: structuredClone(preference) } : { state: "unknown" as const, preference: null });
    },
    preference: (petId, subject, variant) => delay(fixturePreference(petId, subject, variant)),
    recommend: async (body, variant) => delay(fixtureRecommend(body, variant)),
    recommendation: async (id) => delay(fixtureRecommendation(id)),
    feedbackResult: (recommendationId, petId, key) => {
      const feedback = feedbackReceipts.get(JSON.stringify([petId, recommendationId, key]));
      return delay(feedback ? { state: "completed" as const, feedback: structuredClone(feedback) } : { state: "unknown" as const, feedback: null });
    },
    feedback: (body, key) => {
      const recommendation = fixtureRecommendation(body.recommendation_id);
      if (!recommendation.pet_id || recommendation.mode !== "owner_real_dining" || recommendation.data_origin !== "fixture" || recommendation.journey_id !== null) throw ApiError.capability("food.feedback", "请先查看当前伙伴的演示推荐。");
      const saved: FoodFeedback = { ...body, dish_ids: body.dish_ids ?? [], reasons: body.reasons ?? [], note: body.note ?? null, verification: "self_reported", feedback_id: `fx-fb-${Date.now()}` };
      feedbackReceipts.set(JSON.stringify([recommendation.pet_id, body.recommendation_id, key]), structuredClone(saved));
      return delay(saved);
    },
    savePreference: (subject, preference, key) => {
      const saved = { ...preference, version: preference.version + 1, source: "fixture" as const, updated_at: new Date().toISOString() };
      receipts.set(receiptKey(preference.pet_id, subject, key), structuredClone(saved));
      return delay(saved);
    },
    fixtureVariants: () => FOOD_VARIANTS.map((v) => ({ ...v })),
  };
}
